import type {
  UserDetail,
  UserDetailDto,
  UserList,
  UserListDto,
  UserUpdateDto
} from "@src/features/users/types";
import type { AddUserFormData } from "@src/pages/Users/AddUser/types";
import { branchSearchFromDto } from "@src/features/users/lib/branch.ts";
import { rankFromDto } from "@src/features/users/lib/rank.ts";
import {jobTitleFromDto, teamFromDto, titleFromDto} from "@src/features/users";
import {departmentFromDto} from "@src/features/users/lib/department.ts";
import {employmentFromDto} from "@src/features/users/lib/employment.ts";
import {
  type AppLanguage,
  DEFAULT_APP_LANGUAGE,
  getIntlLocaleTag
} from "@src/shared";

function dateToDtoString(date: Date | null): string | undefined {
  if (!date) {
    return undefined;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function hasOwn<T extends object, K extends PropertyKey>(
  value: T,
  key: K,
): value is T & Record<K, unknown> {
  return Object.hasOwn(value, key);
}


export function userListFromDto(user: UserListDto): UserList {
  return {
    id: user.id,
    email: user.email,
    name: user.name,
    firstName: user.user_first_name,
    familyName: user.user_family_name,
    language: user.language,
    isAdmin: user.is_admin,
    manager: user.user_user_manager,
    hr: user.user_user_hr,
    portrait: user.user_portrait,
    jobTitle: user.user_job_title ? jobTitleFromDto(user.user_job_title) : null,
    rank: user.user_rank ? rankFromDto(user.user_rank) : null,
    status: user.status,
    salaryPercentage: user.salary_percentage,
    branch: user.user_branch ? branchSearchFromDto(user.user_branch) : null,
    initials: user.user_initials,
    startDate: user.start_date,
    department: user.user_department,
    team: user.user_team,
    deputy: user.user_user_deputy,
    title: user.user_title,
  };
}

export function userDetailFromDto(user: UserDetailDto): UserDetail {
  const activeSalary = (user.user_salaries || []).find((salary) => salary.is_active);
  const salary = activeSalary ? {
    salaryStartDate: activeSalary.saly_start ? new Date(activeSalary.saly_start) : null,
    salaryEndDate: activeSalary.saly_end ? new Date(activeSalary.saly_end) : null,
    salaryPercentage: activeSalary.saly_percentage,
    salaryVacationAllowance: activeSalary.saly_vacation_allowance,
    salaryMonthly: activeSalary.saly_monthly,
    salaryEmployment: activeSalary.saly_emty ? employmentFromDto(activeSalary.saly_emty) : null,
  } : {
    salaryStartDate: null,
    salaryEndDate: null,
    salaryPercentage: null,
    salaryVacationAllowance: null,
    salaryMonthly: null,
    salaryEmployment: null,
  };
  const emergency = [{
    contact: user.user_emergency_contact1,
    relationship: user.user_emergency_relationship1,
    mobile: user.user_emergency_mobile1,
    email: user.user_emergency_email1,
  }]
  if (
    user.user_emergency_contact2
    || user.user_emergency_relationship2
    || user.user_emergency_mobile2
    || user.user_emergency_email2
  ) {
    emergency.push({
      contact: user.user_emergency_contact2,
      relationship: user.user_emergency_relationship2,
      mobile: user.user_emergency_mobile2,
      email: user.user_emergency_email2,
    })
  }
  return {
    id: user.id,
    email: user.email,
    name: user.name,
    firstName: user.user_first_name,
    familyName: user.user_family_name,
    initials: user.user_initials,
    language: user.language,
    status: user.status,
    isAdmin: user.is_admin,
    branch: user.user_branch ? branchSearchFromDto(user.user_branch) : null,
    portrait: user.user_portrait,
    rank: user.user_rank ? rankFromDto(user.user_rank) : null,
    jobTitle: user.user_job_title ? jobTitleFromDto(user.user_job_title) : null,
    manager: user.user_user_manager,
    hr: user.user_user_hr,
    title: user.user_title ? titleFromDto(user.user_title) : null,
    team: user.user_team ? teamFromDto(user.user_team) : null,
    department: user.user_department ? departmentFromDto(user.user_department) : null,
    deputy: user.user_user_deputy,
    ...salary,
    signature: user.user_signature,
    birthdate: user.user_birthdate ? new Date(user.user_birthdate) : null,
    mobile: user.user_mobile,
    phone: user.user_phone ?? "",
    emailPrivate: user.user_email_private,
    mobilePrivate: user.user_mobile_private,
    phonePrivate: user.user_phone_private,
    addressLine1: user.user_address_line_1,
    addressLine2: user.user_address_line_2,
    city: user.user_city,
    postcode: user.user_postcode,
    country: user.user_country,
    emergency,
  };
}

export function userUpdateToDto(user: AddUserFormData): UserUpdateDto;
export function userUpdateToDto(user: Partial<AddUserFormData>): Partial<UserUpdateDto>;
export function userUpdateToDto(user: Partial<AddUserFormData>) {
  const payload: Partial<UserUpdateDto> = {};

  if (hasOwn(user, 'email')) {
    payload.email = user.email as string;
  }
  if (hasOwn(user, 'firstName')) {
    payload.user_first_name = user.firstName as string;
  }
  if (hasOwn(user, 'familyName')) {
    payload.user_family_name = user.familyName as string;
  }
  if (hasOwn(user, 'initials')) {
    payload.user_initials = user.initials as string;
  }
  if (hasOwn(user, 'language')) {
    payload.language = getIntlLocaleTag(((user.language as string) || DEFAULT_APP_LANGUAGE) as AppLanguage);
  }
  if (hasOwn(user, 'branch')) {
    payload.user_branch = user.branch?.id || null;
  }
  if (hasOwn(user, 'portrait')) {
    payload.user_portrait = user.portrait || null;
  }
  if (hasOwn(user, 'rankId')) {
    payload.user_rank = user.rankId || null;
  }
  if (hasOwn(user, 'jobTitleId')) {
    payload.user_job_title = user.jobTitleId || null;
  }
  if (hasOwn(user, 'manager')) {
    payload.user_user_manager = user.manager?.id || null;
  }
  if (hasOwn(user, 'hr')) {
    payload.user_user_hr = user.hr?.id || null;
  }
  if (hasOwn(user, 'title')) {
    payload.user_title = user.title  || null;
  }
  if (hasOwn(user, 'team')) {
    payload.user_team = user.team?.id || null;
  }
  if (hasOwn(user, 'department')) {
    payload.user_department = user.department?.id || null;
  }
  if (hasOwn(user, 'deputy')) {
    payload.user_user_deputy = user.deputy?.id || null;
  }
  if (hasOwn(user, 'signature')) {
    payload.user_signature = user.signature ?? null;
  }
  if (hasOwn(user, 'birthdate')) {
    payload.user_birthdate = dateToDtoString(user.birthdate ?? null);
  }
  if (hasOwn(user, 'mobile')) {
    payload.user_mobile = user.mobile || null;
  }
  if (hasOwn(user, 'phone')) {
    payload.user_phone = user.phone as string;
  }
  if (hasOwn(user, 'emailPrivate')) {
    payload.user_email_private = user.emailPrivate || null;
  }
  if (hasOwn(user, 'mobilePrivate')) {
    payload.user_mobile_private = user.mobilePrivate || null;
  }
  if (hasOwn(user, 'phonePrivate')) {
    payload.user_phone_private = user.phonePrivate || null;
  }
  if (hasOwn(user, 'addressLine1')) {
    payload.user_address_line_1 = user.addressLine1 || null;
  }
  if (hasOwn(user, 'addressLine2')) {
    payload.user_address_line_2 = user.addressLine2 || null;
  }
  if (hasOwn(user, 'city')) {
    payload.user_city = user.city || null;
  }
  if (hasOwn(user, 'postcode')) {
    payload.user_postcode = user.postcode || null;
  }
  if (hasOwn(user, 'country')) {
    payload.user_country = user.country?.id || null;
  }
  if (hasOwn(user, 'emergency')) {
    payload.user_emergency_contact1 = user.emergency?.[0]?.contact || null;
    payload.user_emergency_relationship1 = user.emergency?.[0]?.relationship || null;
    payload.user_emergency_mobile1 = user.emergency?.[0]?.mobile || null;
    payload.user_emergency_email1 = user.emergency?.[0]?.email || null;
    payload.user_emergency_contact2 = user.emergency?.[1]?.contact || null;
    payload.user_emergency_relationship2 = user.emergency?.[1]?.relationship || null;
    payload.user_emergency_mobile2 = user.emergency?.[1]?.mobile || null;
    payload.user_emergency_email2 = user.emergency?.[1]?.email || null;
  }
  if (hasOwn(user, 'salaryId')) {
    payload.salary_id = user.salaryId;
  }
  if (hasOwn(user, 'salaryStartDate')) {
    payload.salary_saly_start = dateToDtoString(user.salaryStartDate ?? null) as string;
  }
  if (hasOwn(user, 'salaryEndDate')) {
    payload.salary_saly_end = dateToDtoString(user.salaryEndDate ?? null) ?? null;
  }
  if (hasOwn(user, 'salaryPercentage')) {
    payload.salary_saly_percentage = user.salaryPercentage as number;
  }
  if (hasOwn(user, 'salaryMonthly')) {
    payload.salary_saly_monthly = user.salaryMonthly as number;
  }
  if (hasOwn(user, 'salaryVacationAllowance')) {
    payload.salary_saly_vacation_allowance = user.salaryVacationAllowance as number;
  }
  if (hasOwn(user, 'salaryEmployment')) {
    payload.salary_saly_emty = user.salaryEmployment?.id as number;
  }

  return payload;
}
